import { useState, useMemo, useRef } from "react";
import { Menu } from "@mui/material";
import { Typography, Card, CardContent, Box, Chip, Divider, IconButton, Tooltip } from "@mui/material";
import { getIntlTranslation } from "../../../utils/helper";
import { getStatus, fmtDE } from "../utils/statusUtils";
import { parseDefectsString } from "../utils/defectUtils";
import { useIntl } from "react-intl";
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { InfoOutlined } from "@mui/icons-material";
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

const ElementCard = ({ element, handleViewClick, onStatusClick, handleArchiveClick, currentUser, roles }) => {

    const intl = useIntl();
    const expandBtnRef = useRef(null);

    const [defectsAnchorEl, setDefectsAnchorEl] = useState(null);
    const [archiveAnchorEl, setArchiveAnchorEl] = useState(null);

    const defectsOpen = Boolean(defectsAnchorEl);
    const archiveOpen = Boolean(archiveAnchorEl);
    const status = useMemo(
        () =>
            getStatus({
                nextStr: element?.nextInspectionDate,
                lastStr: element?.lastInspectionDate,
                reinspectionStr: element?.reinspectionRequired,
            }),
        [element]
    );
    const defectsText = element?.defectsFound ?? "";
    const hasDefects = !!(defectsText && String(defectsText).trim().length);

    const parsedDefects = useMemo(
        () => (parseDefectsString(defectsText)),
        [defectsText]
    );

    if (!element) return null;

    const value = status
    const textColor =
        value === "completed" ? "#97b444" : "#ffffff";
    const color =
        value === "completed"
            ? "#97b444"
            : value === "overdue"
                ? "#aa3939"
                : "#ffc924";

    return (
        <Card
            sx={{
                mb: 2,
                boxShadow: 4,
                borderRadius: 3,
                width: { xs: '100%', sm: 280, md: 320, lg: 340 },
                maxWidth: '100%',
                height: 260,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                background: '#f9f9fb',
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: 6,
                },
            }}
            onClick={(e) => {
                // prevent navigating when the defects menu is open (menuAnchor is non-null)
                if (defectsOpen || archiveOpen) {
                    e.stopPropagation();
                    return;
                }
                handleViewClick();
            }}
        >
            <CardContent sx={{ pb: 2 }}>
                {currentUser && roles && (roles.includes("ADMIN") || roles.includes("PROJECT LEADER")) ? (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                            size="small"
                            ref={expandBtnRef}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                setArchiveAnchorEl(e.currentTarget); // open archive menu
                            }}
                            aria-label="expand"
                            sx={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}
                        >
                            <MoreHorizIcon />
                        </IconButton>
                    </Box>) : null}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} gap={2}>
                    <Typography variant="h6" sx={{
                        fontWeight: 700,
                        color: "#254a9a",
                        // lineHeight: 1.2,
                        flex: 1,
                        // minWidth: 0,
                        // display: "-webkit-box",
                        // WebkitLineClamp: 2,
                        // WebkitBoxOrient: "vertical",
                        minHeight: "2.4em",
                        mb: 0,
                        alignSelf: 'center'
                    }}>
                        {element.elementName}
                    </Typography>
                    {
                        element?.archiveFlag ?
                            <Chip
                                label={intl.formatMessage({ id: `mi.archived` })}
                                variant='outlined'
                                sx={{
                                    width: 120,
                                    // justifyContent: "center",
                                    fontWeight: 700,

                                    bgcolor: "transparent",
                                    color: "#6b7280",
                                    borderColor: "#6b7280",

                                    cursor: "default"
                                }}
                            /> :
                            <Tooltip title='Complete the Inspection' disableHoverListener={value === 'completed'}
                            >
                                <Chip
                                    clickable={value !== "completed" && currentUser && roles && (roles.includes("ADMIN") || roles.includes("PROJECT LEADER"))}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (value !== "completed" && currentUser && roles && (roles.includes("ADMIN") || roles.includes("PROJECT LEADER")))
                                            onStatusClick();
                                    }}
                                    label={intl.formatMessage({ id: `mi.${value ?? "unknown"}` })}
                                    icon={value !== 'completed' ? <LaunchRoundedIcon color="inherit" fontSize="small" /> : undefined}
                                    size="small"
                                    variant={value !== 'completed' ? "filled" : "outlined"}
                                    sx={{
                                        width: 120,
                                        // justifyContent: "center",
                                        fontWeight: 700,

                                        bgcolor: value !== 'completed' ? color : "transparent",
                                        color: textColor,
                                        borderColor: "#97b444",
                                        // alignSelf: 'center',
                                        cursor: value !== 'completed' && currentUser && roles && (roles.includes("ADMIN") || roles.includes("PROJECT LEADER")) ? "pointer" : "default",

                                        ...(value !== 'completed' && currentUser && roles && (roles.includes("ADMIN") || roles.includes("PROJECT LEADER")) && {
                                            boxShadow: 1,
                                            "&:hover": {
                                                filter: "brightness(0.95)",
                                                color: color,
                                                borderColor: color,
                                            },
                                            "&:active": {
                                                transform: "translateY(1px)",
                                            },
                                            "& .MuiChip-icon": {
                                                transition: "transform 120ms ease",
                                            },
                                            "&:hover .MuiChip-icon": {
                                                transform: "translateX(2px)",
                                                color: color,
                                            },
                                            "&.Mui-focusVisible": {
                                                outline: "none",
                                                boxShadow: `0 0 0 2px ${color}33`,
                                            },
                                        }),
                                        ...(value === 'completed' && {
                                            opacity: 0.9,
                                        }),
                                    }}
                                />
                            </Tooltip>
                    }
                </Box>
                <Divider sx={{ mb: 1 }} />
                <Box mb={1}>
                    {/* Next inspection — PRIMARY */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 0.75, alignItems: 'center' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                color: "text.primary",
                            }}
                        >
                            {getIntlTranslation("mi.next-inspection")}:{" "}

                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                color: "text.primary",
                            }}
                        >
                            {fmtDE(element.nextInspectionDate)}
                        </Typography>
                    </Box>
                    {/* Last inspection — SECONDARY */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 0.75, alignItems: 'center' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                            }}
                        >
                            {getIntlTranslation("mi.last-inspection")}:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                            }}
                        >
                            {fmtDE(element.lastInspectionDate)}
                        </Typography>
                    </Box>

                    {/* Frequency — SECONDARY */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 0.75, alignItems: 'center' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                            }}
                        >
                            {getIntlTranslation("mi.frequency")}:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                            }}
                        >
                            {element.frequencyYears ? `${element.frequencyYears}y` : "-"}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 0.75, alignItems: 'center' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                            }}
                        >
                            {getIntlTranslation("mi.defects-found")}:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                            }}
                        >
                            {hasDefects ? intl.formatMessage({ id: "mi.found" }) : intl.formatMessage({ id: "mi.not-found" })}
                            {hasDefects ? <Tooltip
                                title={'View Defects'}
                                style={{ cursor: "pointer" }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDefectsAnchorEl(e.currentTarget); // open defects menu
                                }}
                            >
                                <InfoOutlined
                                    style={{
                                        fontSize: 16,
                                        marginLeft: 4,
                                        verticalAlign: "middle",
                                    }}
                                />
                            </Tooltip> : ""}
                        </Typography>
                    </Box>
                </Box>
                <Box mb={1}>
                    {archiveOpen && (
                        <Menu
                            anchorEl={archiveAnchorEl}
                            open={archiveOpen}
                            onClose={() => setArchiveAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{ sx: { minWidth: 240, maxHeight: 200, p: 0.5, pl: 2, pr: 1.5, borderRadius: 2, background: '#f5f7fa' } }}
                        >
                            <Box sx={{ mb: 1 }}>
                                <Typography sx={{ p: 1, cursor: 'pointer', color: "#254a9a" }} onClick={(e) => {
                                    e.stopPropagation();
                                    setArchiveAnchorEl(null);      // close menu
                                    handleArchiveClick?.();        // do your action
                                }}>
                                    {element?.archiveFlag ? <UnarchiveOutlinedIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> : <ArchiveOutlinedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />}
                                    {element?.archiveFlag ? intl.formatMessage({ id: `mi.unarchive` }) : intl.formatMessage({ id: `mi.archive` })}
                                </Typography>
                            </Box>
                        </Menu>
                    )}
                </Box>
                <Box mb={1}>
                    <Box mb={1}>
                        {hasDefects && (
                            <Menu
                                anchorEl={defectsAnchorEl}
                                open={defectsOpen}
                                onClose={() => setDefectsAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{ sx: { minWidth: 240, maxHeight: 200, p: 0.5, pl: 2, pr: 1.5, borderRadius: 2, background: '#f5f7fa' } }}
                            >
                                {parsedDefects.length > 0 ? (
                                    parsedDefects.map((text, i) => (
                                        <Box key={i} sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                • {text}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography sx={{ p: 1 }}>{getIntlTranslation("mi.no-defects-found")}</Typography>
                                )}
                            </Menu>
                        )}
                    </Box>

                </Box>
                {Boolean(element.reinspectionRequired?.trim()) ?
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 0.75, alignItems: 'center' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                            }}
                        >
                            {getIntlTranslation("mi.reinspection-required")}:{" "}
                        </Typography>
                        <Box component="span"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (value !== "completed" && currentUser && roles && (roles.includes("ADMIN") || roles.includes("PROJECT LEADER"))) {
                                    onStatusClick();
                                }
                            }}
                        // onMouseDown={(e) => {
                        //     // prevent the mousedown from reaching the Card and causing its onClick
                        //     e.stopPropagation();
                        // }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}
                            >
                                {getIntlTranslation('mi.yes')}, {element.reinspectionRequired}
                            </Typography>
                        </Box>
                    </Box>
                    : null}
            </CardContent>
            {/* <Box sx={{ position: 'absolute', right: 16, bottom: 16 }}>
                <SaveButton
                    id="view-report"
                    buttonText="mi.view"
                    variant="button"
                    size="small"
                    style={{
                        padding: '4px 14px',
                        fontSize: '0.8rem',
                        minWidth: 'auto',
                        lineHeight: 1.25,
                        alignSelf: 'flex-end',
                        background: '#254a9a',
                        color: '#fff',
                        boxShadow: '0 2px 8px rgba(37,74,154,0.12)'
                    }}
                    tooltip={{ title: getIntlTranslation('tooltip.view-site') }}
                    handleClick={(e) => {
                        handleViewClick()
                    }}
                />
            </Box> */}
        </Card>
    );
};

export default ElementCard;